import { type ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { ArrowSquareOut, BookBookmark, Plus, RocketLaunch } from '@phosphor-icons/react';
import { trpc } from 'client/lib/trpc';
import { Button } from 'client/components/Button';
import { Checkbox } from 'client/components/Checkbox';
import { Divider } from 'client/components/Divider';
import { EmptyGitHubNotice } from 'client/components/GitHub/EmptyGitHubNotice';
import { useGitHubAccess } from 'client/components/GitHub/useGitHubAccess';
import { Input } from 'client/components/Input';
import { RadioGroup, type RadioGroupItemProps } from 'client/components/RadioGroup';
import { Stack } from 'client/components/Stack';

const methodItems: RadioGroupItemProps[] = [
  {
    id: 'quick',
    title: 'Quick Launch',
    subtitle: 'Quickly try out this stack without creating a GitHub repository',
    icon: <RocketLaunch />,
  },
  {
    id: 'create',
    title: 'Create Repository',
    subtitle: 'Create an initialize a new GitHub repository',
    icon: <BookBookmark />,
    ornament: <Plus weight="bold" />,
  },
  // {
  //   id: 'existing',
  //   title: 'Pick an existing repository',
  //   subtitle: 'Should be an existing project using the current template',
  //   icon: <BookBookmark />,
  //   ornament: <Eyedropper />,
  //   disabled: true,
  // },
];

type Repository = { owner: string; name: string; isPrivate: boolean };

export const StackRepository = ({
  repository,
  onChange,
}: {
  repository: Repository | null;
  onChange: (repository: Repository | null) => void;
}) => {
  const { authenticate, organizations, githubNoticeVariant, isLoading } = useGitHubAccess({
    scope: ['user', 'organizations'],
  });

  const organizationItems = useMemo(
    () =>
      organizations.map((organization) => ({
        id: organization.name,
        title: organization.name,
        image: { src: organization.image, alt: 'profile picture' },
      })),
    [organizations]
  );

  // If selected organization has been removed, de-select it
  useEffect(() => {
    if (repository && !organizations.some(({ name }) => repository.owner === name)) {
      onChange({ ...repository, owner: '' });
    }
  }, [organizations]);

  const handleMethodChange = useCallback(
    (value: string) => {
      if (value === 'quick') return onChange(null);
      return onChange({ owner: '', name: '', isPrivate: true });
    },
    [onChange]
  );

  const handleOrganizationChange = (value: string) => {
    if (!repository) return;
    return onChange({ ...repository, owner: value });
  };

  const handleRepositoryChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!repository) return;
    return onChange({ ...repository, name: event.target.value });
  };

  const handlePrivateChange = (value: boolean) => {
    if (!repository) return;
    return onChange({ ...repository, isPrivate: value });
  };

  return (
    <div css={containerStyle}>
      <RadioGroup value={repository ? 'create' : 'quick'} items={methodItems} onChange={handleMethodChange} />
      {repository && (
        <>
          <Divider />
          {githubNoticeVariant && <EmptyGitHubNotice variant={githubNoticeVariant} onAction={authenticate} />}
          {!githubNoticeVariant && (
            <>
              <div style={{ alignSelf: 'stretch', marginTop: -24 }}>
                <RadioGroup
                  label="Organization"
                  value={repository.owner || null}
                  items={organizationItems}
                  onChange={handleOrganizationChange}
                />
              </div>
              <Button icon={<ArrowSquareOut />} onClick={authenticate}>
                Install Diploi to more organizations
              </Button>
            </>
          )}
        </>
      )}
      {repository?.owner && (
        <>
          <Divider />
          <Stack css={stackStyle}>
            <Input label="Repository" large value={repository?.name || ''} onChange={handleRepositoryChange} />
            <Checkbox value={repository?.isPrivate || false} onChange={handlePrivateChange}>
              Private repository
            </Checkbox>
          </Stack>
        </>
      )}
    </div>
  );
};

const containerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  > button {
    margin-top: 16px;
  }

  > .divider {
    align-self: stretch;
  }

  > ul {
    align-self: stretch;

    > button {
      position: absolute;
      margin-top: 16px !important;
    }
  }
`;

const stackStyle = css`
  align-self: stretch;
`;
